import { useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';

//components
import withLocalization from '../../../../hoc/withLocalization';
import SwitchWidget from '../../ControlPanel/SwitchWidget';
import { SimpleMultiSelectWidget } from '../../../../components/GenericForm/Widgets/SimpleSelect';
import { SingleUserGroupsWidget } from '../../../../components/GenericForm/Widgets/UserGroupWidget';
import { SingleSkillsWidget } from '../../../../components/GenericForm/Widgets/SkillWidget';
import { Accordion, AccordionItem } from '../../../../components/Accordion/Accordion';
import DatePicker from '~/components/DatePicker/DatePicker.jsx';

//elements
import Select from '../../../../elements/Select';
import { NumberInput } from '../../../../elements/Input';

//elements
import RequiredStar from '../../../../elements/RequiredStar';

//utils
import { timeFormat, getShortDateFormat } from '../../../../utils/dateFormat';
import { DEFAULT_EXTRA_PAYMENTS, isMonday, isSunday } from '../../../../utils/constants';
import {
    buildDepartmentOptions,
    buildProfileSections,
    countProfileFields,
    countManagedProfileFields,
    isProfileFieldManaged,
    setProfileFieldManaged,
    setProfileFieldValue,
    getSectionManagedState,
    setSectionManaged,
    getProfileFieldErrors,
    sectionHasErrors,
} from './permissionGroupUtils';

/** Field errors, listed under the control like the user profile form does. */
const FieldErrors = ({ t, messages }) => (messages && messages.length ? (
    <ul className="text-danger font-size-12 mb-0 mt-1 ps-3">
        {messages.map(message => <li key={message}>{t(message)}</li>)}
    </ul>
) : null);

/** Option lists for the select-style profile fields, read from the client config. */
const buildOptionSources = (config, commonStore, t) => {
    const clientData = config?.client?.data || {};
    const dateTimeRules = clientData.dateTimeRules;

    const departments = buildDepartmentOptions(clientData, t);

    // same list and order as the user profile's Working Shift picker, minus "None" -
    // a group that manages the working shift has to set one
    const companyShifts = commonStore
        .sortCompanyShifts(config?.companyShifts || [], false, { F1: false, F2: false, F3: false, F4: false, F5: false })
        .map(shift => ({
            value: shift.id,
            label: `${shift.shift_name} (${timeFormat(shift.start_time, dateTimeRules)} - ${timeFormat(shift.end_time, dateTimeRules)})`,
        }));

    const leaveTypes = (config?.allAbsentTypes || [])
        .filter(type => type.isDefault === false)
        .map(type => ({ value: type.code, label: t(type.name) }));

    // task-bound extra payments depend on each employee's task assignments, so a
    // group only offers the ones that apply to everyone
    const extraPayments = (clientData.extraPayments || [])
        .filter(payment => payment.isActive && !payment.task && !payment.isMultiTask
            && !DEFAULT_EXTRA_PAYMENTS.includes(payment.code))
        .map(payment => ({ value: payment.code, label: t(payment.name) }));

    // inactive rules stay listed but unselectable, matching how the per-user
    // overtime modal presents them
    const toRuleOption = rule => ({
        value: rule.id,
        label: rule.name + (rule.is_active ? '' : ` (${t('Inactive')})`),
        isDisabled: !rule.is_active,
    });
    const weeklyOvertimeRules = (commonStore.weeklyOvertimeSettings || []).map(toRuleOption);
    const overtimeCapRules = (commonStore.weeklyOvertimeCapSettings || []).map(toRuleOption);

    return {
        departments,
        companyShifts,
        leaveTypes,
        extraPayments,
        weeklyOvertimeRules,
        overtimeCapRules,
        paymentModes: [{ value: 'hourly', label: t('hourly') }, { value: 'fixed', label: t('fixed') }],
        paymentCycles: [{ value: 'monthly', label: t('Monthly') }, { value: 'weekly', label: t('Weekly') }],
    };
};

/**
 * An overtime rule is a dated assignment, so the group stores the rule plus the
 * validity window it should start with. Valid from is required; valid to is left
 * open when blank. Weekly rules must line up Monday-Sunday, which is the same
 * constraint the per-user endpoint enforces.
 */
const OvertimeRuleControl = ({ t, item, profileSettings, optionSources, dateTimeRules, errors, onFieldChange }) => {
    const options = optionSources[item.options] || [];
    const ruleId = profileSettings[item.field] ?? null;
    const validFrom = profileSettings[item.fromField] || null;
    const validTo = profileSettings[item.toField] || null;
    const toDate = value => (value ? new Date(moment(value).format('MM/DD/YYYY')) : null);
    const toIso = value => (value ? moment(value).format('YYYY-MM-DD') : null);

    return (
        <Row className="gx-2">
            <Col xs={12} md={4} className="mb-2 mb-md-0">
                <span className="font-size-10 text-dark d-block mb-1">
                    {t('Rule')}<RequiredStar />
                </span>
                <Select
                    options={options}
                    value={options.find(option => option.value === ruleId) || null}
                    onChange={option => onFieldChange(item.field, option ? option.value : null)}
                    placeholder={t('Select Rule')}
                    style={{ width: '100%' }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
                <FieldErrors t={t} messages={errors[item.field]} />
            </Col>
            <Col xs={12} md={4} className="mb-2 mb-md-0">
                <span className="font-size-10 text-dark d-block mb-1">
                    {t('Valid From')}<RequiredStar />
                </span>
                <DatePicker
                    selected={toDate(validFrom)}
                    placeholder={item.mondayAligned ? t('Select Monday') : t('Select')}
                    dateFormat={getShortDateFormat(dateTimeRules)}
                    onChange={date => onFieldChange(item.fromField, toIso(date))}
                    filterDate={item.mondayAligned ? isMonday : undefined}
                />
                <FieldErrors t={t} messages={errors[item.fromField]} />
            </Col>
            <Col xs={12} md={4}>
                <span className="font-size-10 text-dark d-block mb-1">
                    {t('Valid To')} <span className="text-muted">({t('Optional')})</span>
                </span>
                <DatePicker
                    selected={toDate(validTo)}
                    placeholder={item.mondayAligned ? t('Select Sunday') : t('No end date')}
                    dateFormat={getShortDateFormat(dateTimeRules)}
                    onChange={date => onFieldChange(item.toField, toIso(date))}
                    filterDate={item.mondayAligned ? isSunday : undefined}
                    minDate={toDate(validFrom)}
                />
            </Col>
        </Row>
    );
};

/**
 * Every control renders in the same place - directly under its label - so the
 * values stay aligned down the column whatever the field type is.
 */
const FieldControl = ({ t, item, value, optionSources, invalid, onChange }) => {
    switch (item.control) {
        case 'boolean':
            return <SwitchWidget value={!!value} onChange={checked => onChange(checked)} />;
        case 'number':
            return (
                <NumberInput
                    value={value === null || value === undefined ? '' : value}
                    customClassses={invalid ? 'text-danger' : undefined}
                    min={item.min}
                    max={item.max}
                    step={item.step || 1}
                    onChange={e => onChange(e.target.value === '' ? item.fallback : Number(e.target.value))}
                />
            );
        case 'select': {
            const options = optionSources[item.options] || [];
            const current = value === undefined ? item.fallback : value;
            return (
                <Select
                    options={options}
                    value={options.find(option => option.value === current) || null}
                    onChange={option => onChange(option ? option.value : item.fallback)}
                    placeholder={t('Select')}
                    style={{ width: '100%' }}
                    /* portalled so the open menu floats above the cards instead of
                       overlapping the fields underneath it. The default react-select
                       prefix is kept so the app's option styles (e.g. greyed-out
                       inactive options) still apply. */
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            );
        }
        case 'multiselect':
            return (
                <SimpleMultiSelectWidget
                    options={optionSources[item.options] || []}
                    value={value || ''}
                    onChange={selected => onChange(selected)}
                />
            );
        case 'groups':
            // ignoreCurrentEntity: the group is not tied to one employee, so the
            // widget must not lock groups based on whichever user was opened last
            return <SingleUserGroupsWidget ignoreCurrentEntity value={value || ''} onChange={selected => onChange(selected)} />;
        case 'skills':
            return <SingleSkillsWidget value={value || ''} onChange={selected => onChange(selected)} />;
        default:
            return null;
    }
};

const PermissionGroupProfile = ({ t, commonStore, profileSettings, flags, onChange }) => {
    // read the locale so this observer re-renders on a language switch - `t` itself never changes
    const { config, locale } = commonStore;
    const dateTimeRules = commonStore.getDateTimeRules();
    const sections = useMemo(() => buildProfileSections(flags, profileSettings), [flags, profileSettings]);
    const optionSources = useMemo(() => buildOptionSources(config, commonStore, t), [config, commonStore, t, locale]); // eslint-disable-line react-hooks/exhaustive-deps

    const managedCount = countManagedProfileFields(profileSettings, flags);
    const totalCount = countProfileFields(flags, profileSettings);
    const errors = getProfileFieldErrors(profileSettings, flags);

    if (!sections.length) {
        return (
            <div className="text-center text-dark font-size-12 py-4">
                {t('No onboarding field is available for this company.')}
            </div>
        );
    }

    const handleManagedToggle = (item, managed) => onChange(setProfileFieldManaged(profileSettings, item, managed));
    const handleValueChange = (field, value) => onChange(setProfileFieldValue(profileSettings, field, value));

    return (
        <>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <span className="font-size-12 text-black">
                    <i className="fa fa-info-circle me-1" aria-hidden="true" />
                    {t('Tick a field to let this permission group set it. Unticked fields are left exactly as they are on the employee.')}
                </span>
                <span className="badge rounded-1 text-primary-color">
                    <span className="fw-bold">{managedCount}</span> {t('of')} {totalCount} {t('fields managed')}
                </span>
            </div>

            <Accordion
                className="perm-group-accordion"
                preExpanded={[sections[0].key]}
                allowZeroExpanded
            >
                {sections.map(section => {
                    const state = getSectionManagedState(profileSettings, section);
                    const sectionManaged = section.fields.filter(item => isProfileFieldManaged(profileSettings, item.field)).length;
                    return (
                        <AccordionItem
                            key={section.key}
                            uuid={section.key}
                            title={
                                <span className="d-flex align-items-center gap-2 w-100">
                                    <i className={`fa ${section.icon}`} aria-hidden="true" />
                                    <span>{t(section.label)}</span>
                                    {sectionHasErrors(section, errors) ? (
                                        <i className="fa fa-exclamation-circle text-danger" aria-hidden="true" />
                                    ) : null}
                                    <span className="badge bg-light text-primary-color ms-auto perm-group-section-count">
                                        {sectionManaged}/{section.fields.length} {t('managed')}
                                    </span>
                                </span>
                            }
                        >
                            {section.note ? (
                                <div className="font-size-12 text-dark mb-2">
                                    <i className="fa fa-info-circle me-1" aria-hidden="true" />
                                    {t(section.note)}
                                </div>
                            ) : null}
                            {/* the manage-all tick lives in the panel, not the header - the
                                header is a button, and nesting a checkbox inside it would
                                toggle the accordion on every click. Styled as a toolbar so
                                it never reads as just another field in the list. */}
                            <div className="perm-group-manage-all d-flex align-items-center gap-2 mb-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input mt-0 perm-col-checkbox cursor-pointer"
                                    checked={state === 'all'}
                                    onChange={() => onChange(setSectionManaged(profileSettings, section, state !== 'all'))}
                                />
                                <span
                                    className="font-size-10 text-uppercase fw-600 text-primary-color cursor-pointer"
                                    onClick={() => onChange(setSectionManaged(profileSettings, section, state !== 'all'))}
                                >
                                    {state === 'all' ? t('Stop managing all') : t('Manage all')}
                                </span>
                                <span className="ms-auto font-size-10 text-uppercase text-primary-color">
                                    {sectionManaged}/{section.fields.length} {t('managed')}
                                </span>
                            </div>
                            {section.fields.map(item => {
                                const managed = isProfileFieldManaged(profileSettings, item.field);
                                if (item.control === 'overtime_rule') {
                                    return (
                                        <div key={item.field} className="perm-group-field-row">
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0 perm-col-checkbox cursor-pointer"
                                                    checked={managed}
                                                    onChange={() => handleManagedToggle(item, !managed)}
                                                />
                                                <span
                                                    className="font-size-12 text-black fw-600 cursor-pointer"
                                                    onClick={() => handleManagedToggle(item, !managed)}
                                                >
                                                    {t(item.label)}
                                                </span>
                                            </div>
                                            {managed ? (
                                                <div className="perm-group-rule-body">
                                                    <OvertimeRuleControl
                                                        t={t}
                                                        item={item}
                                                        profileSettings={profileSettings}
                                                        optionSources={optionSources}
                                                        dateTimeRules={dateTimeRules}
                                                        errors={errors}
                                                        onFieldChange={handleValueChange}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                }
                                return (
                                    <Row
                                        key={item.field}
                                        className="gx-2 align-items-center perm-group-field-row"
                                    >
                                        <Col className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input mt-0 perm-col-checkbox cursor-pointer"
                                                checked={managed}
                                                onChange={() => handleManagedToggle(item, !managed)}
                                            />
                                            <span
                                                className="font-size-12 text-black fw-600 cursor-pointer"
                                                onClick={() => handleManagedToggle(item, !managed)}
                                            >
                                                {t(item.label)}
                                                {managed && item.required ? <RequiredStar /> : null}
                                            </span>
                                        </Col>
                                        <Col xs={12} md={5} className="mt-2 mt-md-0">
                                            {managed ? (
                                                <>
                                                    <FieldControl
                                                        t={t}
                                                        item={item}
                                                        value={profileSettings[item.field]}
                                                        optionSources={optionSources}
                                                        invalid={!!errors[item.field]}
                                                        onChange={value => handleValueChange(item.field, value)}
                                                    />
                                                    <FieldErrors t={t} messages={errors[item.field]} />
                                                </>
                                            ) : null}
                                        </Col>
                                    </Row>
                                );
                            })}
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </>
    );
};

export default inject('commonStore')(withLocalization(observer(PermissionGroupProfile)));
